import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloError, useQuery } from '@apollo/client';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import { sharedSceneState } from '../../utils/shareLink';
import { FeatureAccess, NO_ACCESS } from '../../utils/access';
import { useDocumentFormats } from '../../hooks/useDocumentFormats';
import { documentOpensIn, documentViewerRoute } from '../../utils/fileFormats';
import BrandMark from '../../components/common/BrandMark';
import styles from './SharedLinkPage.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

const FILE_TYPE_CONFIG: Record<string, { label: string; icon: IconName; color: string }> = {
  two_d: { label: '2D Drawings', icon: 'architecture', color: '#0061ff' },
  three_d: { label: '3D BIM Models', icon: 'view_in_ar', color: '#7c3aed' },
  document: { label: 'Documents', icon: 'description', color: '#0891b2' },
  point_cloud: { label: 'Point Clouds', icon: 'grain', color: '#059669' },
  indoor: { label: 'Indoor Models', icon: 'location_city', color: '#d97706' },
  gaussian: { label: 'Gaussian Splats', icon: 'blur_on', color: '#4f46e5' },
  orthomosaic: { label: 'Orthomosaics', icon: 'satellite_alt', color: '#0284c7' },
  video360: { label: '360 Videos', icon: 'panorama_photosphere', color: '#b45309' },
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const UNAVAILABLE = 'This sharing link is no longer active or could not be found.';

// NOT_FOUND ("Link not found or expired") also covers revoked links, so only
// the FORBIDDEN expiry error may say the link expired.
const unavailableReason = (error: ApolloError): string => {
  const gqlError = error.graphQLErrors[0];
  const message = gqlError?.message ?? '';
  if (gqlError?.extensions?.code === 'NOT_FOUND' || /not found/i.test(message)) return UNAVAILABLE;
  if (/expired/i.test(message)) return 'This sharing link has expired.';
  if (/limit/i.test(message)) return 'This link has reached its maximum number of views.';
  return UNAVAILABLE;
};

interface SharedFile {
  id: string;
  originalName: string;
  type: string;
  fileSize: number;
  extension: string;
  status: string;
  apsUrn?: string;
  apsStatus?: string;
  panoramaCount?: number;
}

const SharedLinkPage = () => {
  const { token } = useParams<{ token: string }>();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showMatterport, setShowMatterport] = useState(false);
  const documentFormats = useDocumentFormats();

  const toggleSection = (type: string) => {
    setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const { data, loading, error } = useQuery(SHARED_LINK_QUERY, {
    variables: { token },
    fetchPolicy: 'no-cache',
  });

  const link = data?.sharedLink;
  const access: FeatureAccess = link?.ownerAccess ?? NO_ACCESS;

  const filesByType = link?.files?.reduce((acc: Record<string, SharedFile[]>, file: SharedFile) => {
    if (!acc[file.type]) acc[file.type] = [];
    acc[file.type].push(file);
    return acc;
  }, {} as Record<string, SharedFile[]>) ?? {};

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>
            <BrandMark size={34} decorative />
          </div>
          <span className={styles.logoText}>Infrio</span>
        </div>
        {link && (
          <div className={styles.headerMeta}>
            <span className={styles.permBadge} data-perm={link.allowDownload ? 'download' : 'view'}>
              <Icon name={link.allowDownload ? 'download' : 'visibility'} className="text-sm" />
              {link.allowDownload ? 'Download allowed' : 'View only'}
            </span>
            {link.expiresAt && (
              <span className={styles.expiryBadge}>
                <Icon name="schedule" className="text-sm" />
                Expires {formatDate(link.expiresAt)}
              </span>
            )}
          </div>
        )}
      </header>

      <main className={styles.main}>
        {loading && (
          <div className={styles.stateBox}>
            <Icon name="refresh" className="animate-spin text-4xl text-primary" />
            <p className="mt-4 text-on-surface-variant">Loading shared content…</p>
          </div>
        )}

        {!loading && error && (
          <div className={styles.stateBox}>
            <div className={styles.stateIcon}>
              <Icon name="link_off" className="text-3xl" />
            </div>
            <h2 className={styles.stateTitle}>Link unavailable</h2>
            <p className={styles.stateDesc}>{unavailableReason(error)}</p>
          </div>
        )}

        {!loading && link && (
          <div className={styles.content}>
            <div className={styles.projectHeader}>
              <div className={styles.projectIcon}>
                <Icon name="folder_managed" className="text-2xl" />
              </div>
              {link.project.matterportUrl && access.matterport && (
                <button className={styles.matterportBtn} onClick={() => setShowMatterport(true)}>
                  <Icon name="vrpano" className="text-base" />
                  Matterport tour
                </button>
              )}
              <div className={styles.projectInfo}>
                <p className={styles.projectLabel}>Shared Project</p>
                <h1 className={styles.projectName}>{link.project.name}</h1>
                <div className={styles.projectMeta}>
                  {link.project.location && (
                    <span>
                      <Icon name="location_on" className="text-sm" />
                      {link.project.location}
                    </span>
                  )}
                  <span>
                    <Icon name="insert_drive_file" className="text-sm" />
                    {link.files.length} file{link.files.length !== 1 ? 's' : ''}
                  </span>
                  <span>
                    Shared by {link.createdBy.firstName} {link.createdBy.lastName}
                  </span>
                </div>
                {link.project.description && (
                  <p className={styles.projectDesc}>{link.project.description}</p>
                )}
                {link.note && (
                  <div className={styles.note}>
                    <Icon name="chat" className="text-sm" />
                    {link.note}
                  </div>
                )}
                {sharedSceneState(link) === 'open' && (
                  <div className={styles.viewButtonsRow}>
                    <a href={`/share/${token}/scene`} className={styles.smartViewBtn}>
                      <Icon name="3d_rotation" className="text-base" />
                      Open Scene
                    </a>
                  </div>
                )}
              </div>
            </div>

            {link.files.length === 0 ? (
              <div className={styles.emptyFiles}>
                <Icon name="folder_open" className="text-4xl text-outline mb-2 block" />
                <p className="text-on-surface-variant text-sm">No files are available in this shared project.</p>
              </div>
            ) : (
              <div className={styles.fileGroups}>
                {Object.entries(filesByType).map(([type, files]) => {
                  const cfg = FILE_TYPE_CONFIG[type] ?? { label: type, icon: 'insert_drive_file', color: '#6b7280' };
                  const isExpanded = !!expandedSections[type];
                  return (
                    <div key={type} className={styles.fileGroup}>
                      <button className={styles.groupHeader} onClick={() => toggleSection(type)}>
                        <span className={styles.groupIcon} style={{ color: cfg.color, background: `${cfg.color}18` }}>
                          <Icon name={cfg.icon} className="text-lg" />
                        </span>
                        <h2 className={styles.groupTitle}>{cfg.label}</h2>
                        <span className={styles.groupCount}>{(files as SharedFile[]).length}</span>
                        <Icon name="expand_more" className={`text-lg ${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`} />
                      </button>
                      {isExpanded && <div className={styles.fileList}>
                        {(files as SharedFile[]).map((file) => (
                          <div key={file.id} className={styles.fileRow}>
                            <div className={styles.fileIconSmall} style={{ color: cfg.color, background: `${cfg.color}14` }}>
                              <Icon name={cfg.icon} className="text-base" />
                            </div>
                            <div className={styles.fileDetails}>
                              <p className={styles.fileName}>{file.originalName}</p>
                              <p className={styles.fileMeta}>
                                {file.extension.toUpperCase()}
                                {file.fileSize ? ` · ${formatSize(file.fileSize)}` : ''}
                                {file.panoramaCount ? ` · ${file.panoramaCount} panoramas` : ''}
                              </p>
                            </div>
                            <div className={styles.fileActions}>
                              {/* 2D and IFC/RVT are self-hosted (no APS status to gate on — the
                                  viewer handles not-yet-converted itself). RCS has no self-hosted
                                  pipeline: it is viewable only with a finished APS translation
                                  (the share viewer falls back to the legacy Autodesk viewer). */}
                              {(file.type === 'two_d' || (file.type === 'three_d' &&
                                ((file.extension || '').toLowerCase() !== 'rcs' || file.apsStatus === 'success'))) && (
                                <a
                                  href={`/share/${token}/viewer-${file.type === 'two_d' ? '2d' : '3d'}/${file.id}`}
                                  className={styles.downloadBtn}
                                  title={file.type === 'two_d' ? 'Open 2D viewer' : 'Open 3D viewer'}
                                >
                                  <Icon name="visibility" className="text-base" />
                                </a>
                              )}
                              {file.type === 'gaussian' && file.status === 'ready' && access.splat && (
                                <a
                                  href={`/share/${token}/viewer-splat/${file.id}`}
                                  className={styles.downloadBtn}
                                  title="Open photoreal viewer"
                                >
                                  <Icon name="visibility" className="text-base" />
                                </a>
                              )}
                              {file.type === 'orthomosaic' && file.status === 'ready' && access.ortho && (
                                <a
                                  href={`/share/${token}/viewer-ortho/${file.id}`}
                                  className={styles.downloadBtn}
                                  title="Open orthomosaic viewer"
                                >
                                  <Icon name="visibility" className="text-base" />
                                </a>
                              )}
                              {file.type === 'video360' && file.status === 'ready' && access.video360 && (
                                <a
                                  href={`/share/${token}/viewer-360/${file.id}`}
                                  className={styles.downloadBtn}
                                  title="Open 360 video"
                                >
                                  <Icon name="visibility" className="text-base" />
                                </a>
                              )}
                              {file.type === 'document' && documentOpensIn(file.extension, documentFormats) !== 'download' && (
                                <a
                                  href={`/share/${token}/${documentViewerRoute(documentOpensIn(file.extension, documentFormats))}/${file.id}`}
                                  className={styles.downloadBtn}
                                  title={documentOpensIn(file.extension, documentFormats) === 'image' ? 'Open image' : 'Open PDF'}
                                >
                                  <Icon name="visibility" className="text-base" />
                                </a>
                              )}
                              {link.allowDownload && (
                                <a
                                  href={`/api/share/${token}/files/${file.id}/download`}
                                  className={styles.downloadBtn}
                                  download
                                  title="Download file"
                                >
                                  <Icon name="download" className="text-base" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className={styles.footer}>
        <p>Powered by <strong>Infrio</strong> · Secure file sharing for geospatial projects</p>
      </footer>
          {showMatterport && link?.project.matterportUrl && access.matterport && (
        <div className={styles.matterportOverlay} onClick={() => setShowMatterport(false)}>
          <div className={styles.matterportDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.matterportHead}>
              <span>Matterport tour</span>
              <button onClick={() => setShowMatterport(false)} aria-label="Close tour">
                <Icon name="close" />
              </button>
            </div>
            <iframe
              className={styles.matterportFrame}
              src={link.project.matterportUrl}
              title="Matterport tour"
              allowFullScreen
              allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
            />
          </div>
        </div>
      )}
</div>
  );
};

export default SharedLinkPage;
